/* global React, Icon */
const { useState: useStateDir } = React;

// Static shape: IDs, icons, optional flags. All user-visible labels come from window.i18n.t().
// All 11 directions are live. `soon: true` marks a direction that is not yet shipped;
// `hasStatus: true` shows the card's `status` key as a badge — set it only when that
// text is a verifiable fact.
const DIRECTIONS_META = [
  { id: "mammo", icon: "heart" },
  { id: "gyn", icon: "baby" },
  { id: "dent", icon: "tooth" },
  { id: "eye", icon: "eye" },
  { id: "ped", icon: "baby" },
  { id: "lab", icon: "flask" },
  { id: "cos", icon: "sparkle" },
  { id: "gen", icon: "stethoscope" },
  { id: "gastro", icon: "pill" },
  { id: "uro", icon: "shield" },
  { id: "derm", icon: "target" },
];

function Directions() {
  const [active, setActive] = useStateDir("mammo");
  const [mOpen, setMOpen] = useStateDir(false);
  const t = (k) => window.i18n.t(k);

  const buildCard = (meta) => {
    const base = `directions.card.${meta.id}`;
    // Optional keys: pass null fallback so missing-key debug warnings stay silent.
    const feats = [];
    for (let i = 1; i <= 4; i++) {
      const val = window.i18n.t(`${base}.feat.${i}`, null);
      if (val) feats.push(val);
    }
    const scen = [];
    const scenIn = window.i18n.t(`${base}.scen.in`, null);
    if (scenIn) {
      scen.push({
        side: "in",
        text: scenIn,
        label: window.i18n.t(
          `${base}.scen.in.label`,
          window.i18n.t("directions.default.scen.in.label")
        ),
      });
    }
    const scenOut = window.i18n.t(`${base}.scen.out`, null);
    if (scenOut) {
      scen.push({
        side: "out",
        text: scenOut,
        label: window.i18n.t(
          `${base}.scen.out.label`,
          window.i18n.t("directions.default.scen.out.label")
        ),
      });
    }
    const status = meta.hasStatus ? window.i18n.t(`${base}.status`, null) : null;
    return {
      id: meta.id,
      icon: meta.icon,
      soon: meta.soon,
      status,
      label: window.i18n.t(`${base}.label`),
      h: window.i18n.t(`${base}.h`),
      d: window.i18n.t(`${base}.d`),
      feats,
      scen,
    };
  };

  const DIRECTIONS = DIRECTIONS_META.map(buildCard);
  const cur = DIRECTIONS.find((d) => d.id === active) || DIRECTIONS[0];

  return (
    <section
      className="section"
      id="directions"
      style={{
        background: "linear-gradient(180deg, transparent, rgba(44,122,123,0.03) 30%, transparent)",
      }}
    >
      <div className="container">
        <div className="dir-head">
          <div>
            <span className="eyebrow">{t("directions.eyebrow")}</span>
            <h2 style={{ marginTop: 16 }}>{t("directions.h2")}</h2>
          </div>
          <p className="dir-note">{t("directions.note")}</p>
        </div>

        {/* desktop tabs */}
        <div className="dir-tabs" role="tablist">
          {DIRECTIONS.map((d) => (
            <button
              key={d.id}
              role="tab"
              aria-selected={active === d.id}
              className={`dir-tab ${active === d.id ? "active" : ""}`}
              onClick={() => setActive(d.id)}
            >
              <Icon name={d.icon} size={16} />
              {d.label}
              {d.status && (
                <span
                  className="dir-tab-soon"
                  style={{
                    background:
                      active === d.id ? "rgba(255,255,255,0.15)" : "rgba(112,204,138,0.15)",
                    color: active === d.id ? "rgba(255,255,255,0.85)" : "#4B9F66",
                  }}
                >
                  {t("directions.badge.live")}
                </span>
              )}
              {d.soon && <span className="dir-tab-soon">{t("directions.badge.soon")}</span>}
            </button>
          ))}
        </div>

        {/* mobile dropdown */}
        <div className="dir-select">
          <button className="dir-select-btn" onClick={() => setMOpen(!mOpen)} aria-expanded={mOpen}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <Icon name={cur.icon} size={18} color="var(--coral-ink)" />
              {cur.label}
              {cur.status && (
                <span
                  className="dir-tab-soon"
                  style={{ background: "rgba(112,204,138,0.15)", color: "#4B9F66" }}
                >
                  {t("directions.badge.live")}
                </span>
              )}
            </span>
            <Icon name="chevDown" size={18} />
          </button>
          {mOpen && (
            <div className="dir-select-list">
              {DIRECTIONS.map((d) => (
                <button
                  key={d.id}
                  className={active === d.id ? "active" : ""}
                  onClick={() => {
                    setActive(d.id);
                    setMOpen(false);
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                    <Icon name={d.icon} size={16} color="var(--coral-ink)" />
                    {d.label}
                  </span>
                  {d.status && (
                    <span
                      className="dir-tab-soon"
                      style={{ background: "rgba(112,204,138,0.15)", color: "#4B9F66" }}
                    >
                      {t("directions.badge.live")}
                    </span>
                  )}
                  {d.soon && <span className="dir-tab-soon">{t("directions.badge.soon")}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="dir-panel">
          <div className="dir-copy">
            {cur.status && (
              <span className="dir-badge">
                <span
                  style={{ width: 6, height: 6, borderRadius: "50%", background: "#4B9F66" }}
                ></span>
                {cur.status}
              </span>
            )}
            <h3>{cur.h}</h3>
            <p className="dir-desc">{cur.d}</p>
            <ul className="dir-feat">
              {cur.feats.map((f, i) => (
                <li key={i}>
                  <Icon name="check" size={18} stroke={2} />
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="dir-side">
            <h4>{t("directions.side.title")}</h4>
            <div className="dir-scen">
              {(cur.scen.length
                ? cur.scen
                : [
                    {
                      side: "in",
                      text: t("directions.default.scen.in"),
                      label: t("directions.default.scen.in.label"),
                    },
                    {
                      side: "out",
                      text: t("directions.default.scen.out"),
                      label: t("directions.default.scen.out.label"),
                    },
                  ]
              ).map((b, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: b.side === "in" ? "flex-start" : "flex-end",
                  }}
                >
                  <div className="label">{b.label}</div>
                  <div className={`bub ${b.side}`}>{b.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

window.Directions = Directions;
